import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import { apiV2 } from "api/firebase";

import {
  INotificationBaseRelatedToShow,
  ISignature,
  NotificationType,
} from "@evennt-tech/api-v2-core";

import {
  ICreateSignatureReq,
  ICreateSignatureRes,
  IDeleteSignatureReq,
  IGetAllSignatureReq,
  IUpdateSignatureReq,
  IUpdateSignatureRes,
} from "@evennt-tech/api-v2-external";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  selected: ISignature | null;
  items: Record<string, Array<ISignature>>;
  isLoading: boolean;
};

type Action = {
  fetch: (data: IGetAllSignatureReq) => Promise<void>;
  fetchOne: (signatureId: string, showId: string) => Promise<void>;
  update: (data: IGetAllSignatureReq) => Promise<void>;
  autoCatch: (data: IGetAllSignatureReq) => Promise<void>;
  create: (
    body: ICreateSignatureReq
  ) => Promise<ICreateSignatureRes | undefined>;
  edit: (body: IUpdateSignatureReq) => Promise<IUpdateSignatureRes | undefined>;
  remove: (params: IDeleteSignatureReq) => Promise<void>;
  getOne: () => ISignature | null;
  getByShowId: (showId: string) => Array<ISignature>;
  getAll: () => Record<string, Array<ISignature>>;
  clearSelected: () => void;
};

export const useSignatures = create<State & Action>()(
  devtools((set, get) => ({
    items: {} as Record<string, Array<ISignature>>,
    isLoading: false as boolean,
    selected: null as ISignature | null,

    getOne: () => get().selected,

    getAll: () => get().items,

    clearSelected: () => set({ selected: null }),

    getByShowId: (showId) => get().items[showId] || [],

    autoCatch: async ({ showId, shouldBeShownInList }) => {
      const { update } = get();

      IdleManager.getInstance().addSubscriber(
        `signature-${showId}`,
        async () => {
          await update({ showId, shouldBeShownInList });
        }
      );

      const keys = [
        NotificationType.CREATED_SIGNATURE,
        NotificationType.UPDATED_SIGNATURE,
        NotificationType.DELETED_SIGNATURE,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
          key,
          showId,
          callback: async () => {
            await update({ showId, shouldBeShownInList });
          },
        });
      }
    },

    update: async ({ showId, shouldBeShownInList }) => {
      const { items } = get();
      try {
        const signatures = await apiV2.signature.getAll({
          showId,
          shouldBeShownInList,
        });

        set({ items: { ...items, [showId]: signatures } });
      } catch (e) {
        console.error("signature store update:", e);
      }
    },

    fetch: async ({ showId, shouldBeShownInList }) => {
      const { items, isLoading, update } = get();

      if (items[showId] || isLoading) return;

      set({ isLoading: true });
      try {
        await update({ showId, shouldBeShownInList });
      } catch (e) {
        console.error("signature store fetch:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    fetchOne: async (signatureId, showId) => {
      try {
        const signature = await apiV2.signature.getOne({
          id: signatureId,
          showId,
        });

        if (!signature) {
          set({ selected: null });
          return;
        }

        set({ selected: signature });
      } catch (error) {
        console.error("Failed to fetch one signature:", error);
        set({ selected: null });
      }
    },

    create: async (body) => {
      set({ isLoading: true });
      try {
        const res = await apiV2.signature.create(body);
        // The per-show cache holds ONLY library signatures
        // (`shouldBeShownInList: true`) — every fetch/update passes that flag,
        // and both the settings list and the reviewer reuse picker read it. A
        // verdict-time "dummy" (saveSignature=false) is referenced only by
        // `task_signatures`; appending it here leaks it into the picker for the
        // reviewer's next approvals. Only cache it when it's a library entry.
        if (res.shouldBeShownInList) {
          const current = get().items[body.showId] || [];
          set((state) => ({
            items: { ...state.items, [body.showId]: [...current, res] },
          }));
        }
        set({ isLoading: false });
        return res;
      } catch (error) {
        set({ isLoading: false });
        console.error("Failed to create signature:", error);
      }
    },

    edit: async (body) => {
      set({ isLoading: true });
      try {
        const res = await apiV2.signature.update(body);
        const current = get().items[res.showId] || [];
        set((state) => ({
          items: {
            ...state.items,
            [res.showId]: current.map((item) =>
              item.id === res.id ? res : item
            ),
          },
          isLoading: false,
        }));
        return res;
      } catch (error) {
        set({ isLoading: false });
        console.error("Failed to edit signature:", error);
      }
    },

    remove: async ({ signatureId, showId }) => {
      try {
        await apiV2.signature.delete({ signatureId, showId });
        const current = get().items[showId] || [];
        set((state) => ({
          items: {
            ...state.items,
            [showId]: current.filter((item) => item.id !== signatureId),
          },
        }));
      } catch (error) {
        console.error("Failed to delete signature:", error);
      }
    },
  }))
);

registerStore(useSignatures);
